import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ErrorBoundary from '../../ErrorBoundary';
import SubmitButton from '../formComponents/SubmitButton';
import ErrorMessage from '../formComponents/ErrorMessage';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import { db, functions } from '../../../firebase-client';
const exportMembersFn = httpsCallable(functions, 'exportTeamMembersCsv');
const FIELD_DEFS = [{
  key: 'firstName',
  label: 'First name'
}, {
  key: 'lastName',
  label: 'Surname'
}, {
  key: 'dob',
  label: 'DOB'
}, {
  key: 'email',
  label: 'Email'
}, {
  key: 'joinedAt',
  label: 'Date joined'
}, {
  key: 'membershipType',
  label: 'Membership type'
}, {
  key: 'membershipPlan',
  label: 'Membership plan'
}, {
  key: 'membershipTags',
  label: 'Membership tags'
}, {
  key: 'familyMembershipRole',
  label: 'Family membership role'
}, {
  key: 'membershipCostPaid',
  label: 'Membership cost paid',
  requiresFinance: true
}];
const DEFAULT_SELECTION = FIELD_DEFS.reduce((acc, field) => {
  acc[field.key] = true;
  return acc;
}, {});
export default function ExportMembersForm({
  isOpen,
  onClose,
  teamId,
  canExportFinancialFields = false
}) {
  const [selectedFields, setSelectedFields] = useState(DEFAULT_SELECTION);
  const [selectedCustomFields, setSelectedCustomFields] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  useEffect(() => {
    if (!isOpen || !teamId) return;
    async function fetchCustomFields() {
      try {
        const snap = await getDoc(doc(db, 'teams', teamId, 'profileFields', 'config'));
        if (!snap.exists()) {
          setCustomFields([]);
          setSelectedCustomFields({});
          return;
        }
        const data = snap.data();
        const fields = Array.isArray(data.fields) ? data.fields : [];
        const enabled = data.enabled !== false;
        const usable = enabled ? fields : [];
        setCustomFields(usable);
        const defaults = usable.reduce((acc, field) => {
          acc[field.id] = !!field.required;
          return acc;
        }, {});
        setSelectedCustomFields(defaults);
      } catch (err) {
        setCustomFields([]);
        setSelectedCustomFields({});
      }
    }
    fetchCustomFields();
  }, [isOpen, teamId]);
  const visibleFields = useMemo(
    () => FIELD_DEFS.filter(field => !field.requiresFinance || canExportFinancialFields),
    [canExportFinancialFields]
  );
  const selectedKeys = useMemo(() => visibleFields.filter(field => selectedFields[field.key]).map(field => field.key), [selectedFields, visibleFields]);
  const selectedCustomKeys = useMemo(() => customFields.filter(field => selectedCustomFields[field.id]).map(field => field.id), [customFields, selectedCustomFields]);
  const toggleField = key => {
    setSelectedFields(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const toggleCustomField = key => {
    setSelectedCustomFields(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  const triggerDownload = (csvText, fileName) => {
    const blob = new Blob([csvText], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'members-export.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };
  const handleExport = async () => {
    if (!selectedKeys.length && !selectedCustomKeys.length) {
      setError('Choose at least one field to export.');
      return;
    }
    setError('');
    setSuccessMessage('');
    setExporting(true);
    try {
      const result = await exportMembersFn({
        teamId,
        fields: selectedKeys,
        customFieldIds: selectedCustomKeys
      });
      const csv = result?.data?.csv || '';
      const fileName = result?.data?.fileName || 'members-export.csv';
      const count = result?.data?.count ?? 0;
      if (!csv) {
        throw new Error('Export failed. Please try again.');
      }
      triggerDownload(csv, fileName);
      setSuccessMessage(`Downloaded ${count} member${count === 1 ? '' : 's'} as CSV.`);
    } catch (err) {
      setError(err?.message || 'Failed to export members.');
    } finally {
      setExporting(false);
    }
  };
  if (!isOpen) return null;
  return <div className="modal-form">
      <h2 className="modal-title">Export Members</h2>
      <p>Select the details you want in your CSV export.</p>

      <div style={{
      display: 'grid',
      gap: '0.5rem',
      marginTop: '1rem'
    }}>
        {visibleFields.map(field => <label key={field.key} style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}>
            <input type="checkbox" checked={!!selectedFields[field.key]} onChange={() => toggleField(field.key)} />
            <span>{field.label}</span>
          </label>)}
      </div>

      {customFields.length > 0 && <>
          <p style={{
        marginTop: '1rem',
        marginBottom: '0.5rem'
      }}>Team profile fields</p>
          <div style={{
        display: 'grid',
        gap: '0.5rem'
      }}>
            {customFields.map(field => <label key={field.id} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
                <input type="checkbox" checked={!!selectedCustomFields[field.id]} onChange={() => toggleCustomField(field.id)} />
                <span>{field.label}</span>
              </label>)}
          </div>
        </>}

      <div className="mt-4 flex justify-end gap-2">
        <SubmitButton type="button" label="Cancel" onClick={onClose} disabled={exporting} variant="ghost" size="sm" />
        <ErrorBoundary label="SubmitButton">
          <SubmitButton type="button" onClick={handleExport} label={exporting ? 'Exporting...' : `Export ${selectedKeys.length + selectedCustomKeys.length} field${selectedKeys.length + selectedCustomKeys.length === 1 ? '' : 's'}`} disabled={exporting} variant="primary" />
        </ErrorBoundary>
      </div>

      {successMessage && <ErrorBoundary label="SuccessMessage">
          <p style={{
        marginTop: '0.75rem',
        color: '#1b6f4d'
      }}>{successMessage}</p>
        </ErrorBoundary>}

      {error && <ErrorBoundary label="ErrorMessage">
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorBoundary>}
    </div>;
}
ExportMembersForm.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  teamId: PropTypes.string.isRequired,
  canExportFinancialFields: PropTypes.bool
};
